/** @jsx jsx */
import React, { type ChangeEvent, useCallback, useMemo, type PropsWithChildren } from 'react';
import { css, jsx, cssMap } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Avatar from '@atlaskit/avatar';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { ModalEntryPointIconButtonTrigger } from '@atlassian/jira-modal-entry-point-icon-button-trigger/src/ModalEntryPointIconButtonTrigger.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { TimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter/src/types.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import type { EstimationType } from '../../../model/types.tsx';
import messages from './messages.tsx';
import { workloadByAssigneeEntryPoint } from './summary-modal/entrypoint.tsx';
import TooltipContent from './tooltip-content/index.tsx';
import type { Assignee } from './types.tsx';
import UnshownAvatarCount from './unshown-count/index.tsx';

const wrapperStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'start',
	flexGrow: 1,
	overflow: 'auto',
	marginTop: token('space.050'),
	marginRight: token('space.100'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingLeft: '14px',
});

const styles = cssMap({
	wrapperStyles: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'start',
		flexGrow: 1,
		overflow: 'auto',
		marginTop: token('space.050'),
		marginRight: token('space.100'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingLeft: '14px',
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
	},
});

const containerStyles = css({
	display: 'flex',
	alignItems: 'center',
	// TODO Delete this comment after verifying space token -> previous value ``${gridSize * 0.5}px``
	gap: token('space.050'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	div: {
		display: 'flex',
		alignItems: 'center',
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	span: {
		boxShadow: 'none',
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'#entryPointButtonTrigger': {
		padding: '0 3px',
		alignItems: 'center',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		span: {
			display: 'flex',
		},
	},
});

type Props = {
	assignees: Assignee[];
	unassigned?: Assignee;
	estimationType: EstimationType;
	isEstimationEnabled: boolean;
	isRemainingTimeTrackingEnabled: boolean;
	sprintName: string;
	timeTrackingOptions: TimeTrackingOptions;
	estimationFieldName: string;
	includeSubtasks: boolean;
	onToggleIncludeSubtasks: (
		event: ChangeEvent<HTMLInputElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
};

const IconComponent = () => (
	<ShowMoreHorizontalIcon
		label="More actions"
		color={token('color.icon')}
		spacing="spacious"
		LEGACY_fallbackIcon={MoreIcon}
		LEGACY_primaryColor={token('color.icon')}
	/>
);

const WorkloadByAssigneeWrapper = ({
	isVisuallyRefreshed,
	children,
}: PropsWithChildren<{ isVisuallyRefreshed: boolean }>) => {
	if (isVisuallyRefreshed) {
		return <div css={styles.wrapperStyles}>{children}</div>;
	}

	return <div css={wrapperStyles}>{children}</div>;
};

const WorkLoadByAssigneeView = ({
	assignees,
	estimationType,
	isEstimationEnabled,
	isRemainingTimeTrackingEnabled,
	sprintName,
	unassigned,
	timeTrackingOptions,
	estimationFieldName,
	includeSubtasks,
	onToggleIncludeSubtasks,
}: Props) => {
	const { formatMessage } = useIntl();

	const onOpenModal = useCallback((event: React.MouseEvent, analyticsEvent?: UIAnalyticsEvent) => {
		analyticsEvent && fireUIAnalytics(analyticsEvent, 'openWorkloadModal');
	}, []);

	const firstTenAssignees = useMemo(() => assignees.slice(0, 10), [assignees]);
	const assigneeCount = assignees.length;
	const unshownCount = assigneeCount > 10 ? assigneeCount - 10 : undefined;

	const onShowTooltip = (analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'workloadTooltipViewed');
	};

	const summaryModalProps = {
		sprintName,
		assignees,
		unassigned,
		isEstimationEnabled,
		isRemainingTimeTrackingEnabled,
		estimationType,
		timeTrackingOptions,
		estimationFieldName,
		includeSubtasks,
		onToggleIncludeSubtasks,
	};

	const modalProps = useMemo(() => ({ width: 'medium' }), []);

	return (
		<WorkloadByAssigneeWrapper isVisuallyRefreshed={isVisualRefreshEnabled()}>
			<div css={containerStyles}>
				{firstTenAssignees.map((assignee, index) => (
					<Tooltip
						onShow={onShowTooltip}
						key={`assignee-tool-tip-${index}`}
						content={
							<TooltipContent
								username={assignee.name ?? ''}
								storyPoints={assignee.storyPoints}
								remainingTimeEstimateInSeconds={assignee.remainingTimeEstimateInSeconds}
								issueCount={assignee.issueCount}
								estimationType={estimationType}
								isEstimationEnabled={isEstimationEnabled}
								isRemainingTimeTrackingEnabled={isRemainingTimeTrackingEnabled}
								originalTimeEstimateInSeconds={assignee.originalTimeEstimateInSeconds}
								timeTrackingOptions={timeTrackingOptions}
								estimationFieldName={estimationFieldName}
							/>
						}
						position="bottom-start"
					>
						<Avatar
							src={assignee.profilePicture}
							appearance="circle"
							name={assignee.name}
							size="small"
							borderColor="transparent"
						/>
					</Tooltip>
				))}
				{unshownCount && <UnshownAvatarCount count={unshownCount} onClick={onOpenModal} />}
				<ModalEntryPointIconButtonTrigger
					appearance="subtle"
					entryPoint={workloadByAssigneeEntryPoint}
					entryPointProps={summaryModalProps}
					interactionName="open-work-load-by-assignee-modal"
					id="entryPointButtonTrigger"
					testId="software-backlog.card-list.work-load-by-assignee.entry-point-button-trigger"
					icon={IconComponent}
					label={formatMessage(messages.openModalButtonAriaLabel)}
					onClick={onOpenModal}
					modalProps={modalProps}
					useInternalModal
				/>
			</div>
		</WorkloadByAssigneeWrapper>
	);
};

export default WorkLoadByAssigneeView;
